import React from 'react'
import { AlertCircle, Eye, EyeOff } from 'lucide-react'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Input variant style
   */
  variant?: 'default' | 'filled' | 'outline'
  /**
   * Input size
   */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Error state
   */
  error?: boolean
  /**
   * Error message
   */
  errorMessage?: string
  /**
   * Success state
   */
  success?: boolean
  /**
   * Success message
   */
  successMessage?: string
  /**
   * Helper text
   */
  helperText?: string
  /**
   * Left icon
   */
  leftIcon?: React.ReactNode
  /**
   * Right icon
   */
  rightIcon?: React.ReactNode
  /**
   * Show password toggle for password inputs
   */
  showPasswordToggle?: boolean
  /**
   * Label text
   */
  label?: string
  /**
   * Required field indicator
   */
  required?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      variant = 'default',
      size = 'md',
      error = false,
      errorMessage,
      success = false,
      successMessage,
      helperText,
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      label,
      required = false,
      className = '',
      type = 'text',
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false)
    const [, setIsFocused] = React.useState(false)

    const inputType = type === 'password' && showPassword ? 'text' : type

    const baseClasses = [
      'w-full',
      'rounded-lg',
      'border',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-0',
      'disabled:opacity-50',
      'disabled:cursor-not-allowed',
      'placeholder:text-gray-400'
    ]

    const variantClasses = {
      default: [
        'bg-white',
        'border-gray-300',
        'focus:border-primary-500',
        'focus:ring-primary-500'
      ],
      filled: [
        'bg-gray-50',
        'border-gray-200',
        'focus:bg-white',
        'focus:border-primary-500',
        'focus:ring-primary-500'
      ],
      outline: [
        'bg-transparent',
        'border-gray-300',
        'focus:border-primary-500',
        'focus:ring-primary-500'
      ]
    }

    const sizeClasses = {
      sm: ['px-3', 'py-1.5', 'text-sm'],
      md: ['px-4', 'py-2.5', 'text-base'],
      lg: ['px-4', 'py-3', 'text-lg']
    }

    const stateClasses = [
      error && [
        'border-red-500',
        'focus:border-red-500',
        'focus:ring-red-500'
      ],
      success && [
        'border-green-500',
        'focus:border-green-500',
        'focus:ring-green-500'
      ]
    ].filter(Boolean).flat()

    const inputClasses = [
      ...baseClasses,
      ...variantClasses[variant],
      ...sizeClasses[size],
      ...stateClasses,
      leftIcon && 'pl-10',
      // Extra space to avoid text overlapping the end icon
      (rightIcon || showPasswordToggle) && 'pr-12',
      className
    ].join(' ')

    const handlePasswordToggle = () => {
      setShowPassword(!showPassword)
    }

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 mr-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {leftIcon}
            </div>
          )}
          
          <input
            ref={ref}
            type={inputType}
            className={inputClasses}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />
          
          {showPasswordToggle && type === 'password' && (
            <button
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={handlePasswordToggle}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          )}
          
          {rightIcon && !showPasswordToggle && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>
        
        {/* Helper text, error, or success message */}
        {(helperText || errorMessage || successMessage) && (
          <div className="mt-1 flex items-center gap-1">
            {error && errorMessage && (
              <>
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-600">{errorMessage}</p>
              </>
            )}
            
            {success && successMessage && (
              <p className="text-sm text-green-600">{successMessage}</p>
            )}
            
            {!error && !success && helperText && (
              <p className="text-sm text-gray-500">{helperText}</p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }
